import React, {useEffect, useState} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ChoiceChips from "@/components/ChoiceChips";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";

import {MedicalAPI} from "@/api/medical";
import {StaffAPI} from "@/api/staff";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";
import {
	checkRequiredFields,
	getTextField,
	getTextAreaField,
} from "@/utils/functions";

const KINDS = [
	{value: "injury", label: "Infortunio"},
	{value: "illness", label: "Malattia"},
];

const HandleMedicalInjuryPage = (props) => {
	const {handleNotification, edit} = props;

	const navigate = useNavigate();
	const {id, injuryId} = useParams();
	const [searchParams] = useSearchParams();

	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({});
	const [kind, setKind] = useState(
		searchParams.get("tipo") === "malattia" ? "illness" : "injury",
	);
	const isInjury = kind === "injury";
	const recordPath = "/medical/records/" + id;

	const [recordData, setRecordData] = useState({});
	const getRecordById = async () => {
		try {
			const response = await MedicalAPI.recordGetById(id);
			setRecordData(response || {});
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	useEffect(() => {
		if (id) {
			getRecordById();
		}
	}, [id]);

	const [staffOptions, setStaffOptions] = useState([]);
	useEffect(() => {
		StaffAPI.list()
			.then((staffs) => {
				setStaffOptions(
					(staffs || []).map((staff) => ({
						value: staff.id,
						label: [staff.firstName, staff.lastName].filter(Boolean).join(" "),
					})),
				);
			})
			.catch(() => handleNotification(ERROR_OPERATION, "error"));
	}, []);

	useEffect(() => {
		if (!edit) return;
		const loadInjury = async () => {
			setLoading(true);
			setLoadError(false);
			try {
				const injury = await MedicalAPI.injuryGetById(id, injuryId);
				const nextFields = {...injury};
				setKind(injury.recordType ? "illness" : "injury");
				["startDate", "medicalDischargeDate", "sportsDischargeDate"].forEach(
					(key) => {
						nextFields[key] = injury[key] ? new Date(injury[key]) : null;
					},
				);
				setFields(nextFields);
			} catch {
				setLoadError(true);
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		};
		loadInjury();
	}, [edit, id, injuryId]);

	const saveInjury = async () => {
		const errors = checkRequiredFields(["startDate", "diagnosis"], fields);
		setErrors(errors);
		if (errors.length) return;

		setLoading(true);
		const body = {
			...fields,
			recordType: !isInjury,
			medicalRecordId: id,
			staffId: fields.staffId ?? null,
			medicalDischargeDate: fields.medicalDischargeDate ?? null,
			sportsDischargeDate: fields.sportsDischargeDate ?? null,
		};
		try {
			edit
				? await MedicalAPI.injuryUpdate(id, injuryId, body)
				: await MedicalAPI.injuryCreate(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate(recordPath);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () =>
				navigate(edit ? recordPath + "/injuries/" + injuryId : recordPath),
			color: "info",
		},
		{icon: <SaveIcon />, title: "Salva", onClick: saveInjury},
	];

	const breadcrumbsElements = [
		{label: "Cartelle", path: "/medical/records"},
		{label: "/"},
		{
			label: [
				recordData?.playerData?.firstName,
				recordData?.playerData?.lastName,
			]
				.filter(Boolean)
				.join(" "),
			path: recordPath,
		},
		{label: "/"},
	];

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	if (loadError)
		return (
			<Page>
				<div>
					Impossibile caricare {isInjury ? "l’infortunio" : "la malattia"}.
				</div>
			</Page>
		);

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					(edit ? "Modifica " : "Nuov" + (isInjury ? "o " : "a ")) +
					(isInjury ? "infortunio" : "malattia")
				}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title={isInjury ? "Infortunio" : "Malattia"}
							content={
								<div className="row">
									{!edit && (
										<div className="col-12">
											<div className="field-label-container">
												<div className="field-label">Tipo</div>
											</div>
											<ChoiceChips
												options={KINDS}
												value={kind}
												onChange={setKind}
											/>
										</div>
									)}
									<div className="col-sm-12 col-md-6">
										<VLFDatePicker
											label="Inizio"
											value={fields.startDate}
											onChange={(value) =>
												setFields({...fields, startDate: value})
											}
											required
											error={errors.includes("startDate")}
											size="small"
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField({
											...defaultTextField,
											fieldKey: "diagnosis",
											label: "Diagnosi",
											required: true,
										})}
									</div>
									{isInjury &&
										[
											["injuryType", "Tipo di infortunio"],
											["bodyPart", "Parte del corpo"],
											["cause", "Causa"],
										].map(([fieldKey, label]) => (
											<div key={fieldKey} className="col-sm-12 col-md-6">
												{getTextField({...defaultTextField, fieldKey, label})}
											</div>
										))}
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
				<div className="row no-margin mt-3">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Cura e rientro"
							content={
								<div className="row">
									<div className="col-sm-12 col-md-6">
										{getTextField({
											...defaultTextField,
											fieldKey: "exam",
											label: "Esame",
										})}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField({
											...defaultTextField,
											fieldKey: "therapy",
											label: "Terapia",
										})}
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFDatePicker
											label="Dimissione medica"
											value={fields.medicalDischargeDate}
											onChange={(value) =>
												setFields({...fields, medicalDischargeDate: value})
											}
											size="small"
										/>
									</div>
									{isInjury && (
										<div className="col-sm-12 col-md-6">
											<VLFDatePicker
												label="Rientro in campo (dimissione sportiva)"
												value={fields.sportsDischargeDate}
												onChange={(value) =>
													setFields({...fields, sportsDischargeDate: value})
												}
												size="small"
											/>
										</div>
									)}
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											label="Staff (facoltativo)"
											placeholder="Nessuno"
											options={staffOptions}
											value={
												staffOptions.find(
													(staff) => staff.value === fields.staffId,
												) || null
											}
											onChange={(staff) =>
												setFields({...fields, staffId: staff?.value ?? null})
											}
										/>
									</div>
									<div className="col-12">
										{getTextAreaField({
											...defaultTextField,
											fieldKey: "notes",
											label: "Note",
											minRows: 4,
											maxRows: 10,
										})}
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default HandleMedicalInjuryPage;
